import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import visitService from '../../services/visitService';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import { formatActionItemStatusForDisplay } from '../../utils/actionItemUi';
import { splitLetterContent, normalizeLetterKind } from '../../utils/visitLetterDisplay';
import '../Visits/GenerateVisitLetterModal.css';
import './visitLetterPreviewPanel.css';

const getVisitRowId = (v) =>
  Number(v?.scheduledVisitID ?? v?.scheduledVisitId ?? v?.ScheduledVisitID ?? 0);

const getIssueScheduledVisitId = (item) =>
  Number(item?.scheduledVisitID ?? item?.ScheduledVisitID ?? item?.scheduledVisitId ?? 0);

const isActionItemDbClosed = (status) =>
  String(status ?? '').trim().toLowerCase() === 'closed';

const getActionItemDedupeKey = (item) => {
  const id = item?.actionItemID ?? item?.nCRFActionItemNo;
  if (id != null && String(id).trim() !== '') return `id:${String(id).trim()}`;
  const ref = item?.actionItemNumber ?? item?.vActionItemNumber;
  if (ref != null && String(ref).trim() !== '') return `ref:${String(ref).trim()}`;
  return `row:${getIssueScheduledVisitId(item)}:${String(item?.actionItemOn ?? '').trim()}:${String(item?.findingDescription ?? '').slice(0, 40)}`;
};

const isSsvVisitType = (visitType) =>
  String(visitType ?? '').trim().toUpperCase() === 'SSV';

const visitMatchesLetterPickerScope = (letterVisit, apiVisit) => {
  const letterStudy = Number(letterVisit?.studyID ?? letterVisit?.StudyID ?? 0);
  const vStudy = Number(apiVisit?.studyID ?? apiVisit?.StudyID ?? 0);
  if (letterStudy > 0 && vStudy > 0 && letterStudy === vStudy) return true;
  const letterPc = String(letterVisit?.projectCode ?? letterVisit?.ProjectCode ?? '').trim().toLowerCase();
  const vPc = String(apiVisit?.projectCode ?? apiVisit?.ProjectCode ?? '').trim().toLowerCase();
  return letterPc !== '' && letterPc === vPc;
};

const buildSiteVisitsPickerList = (sameProjectVisits, currentVisit) => {
  const byId = new Map();
  (sameProjectVisits || []).forEach((v) => {
    const id = getVisitRowId(v);
    if (id > 0) byId.set(id, v);
  });
  const curId = getVisitRowId(currentVisit);
  if (curId > 0 && !byId.has(curId)) {
    byId.set(curId, currentVisit);
  }
  return Array.from(byId.values());
};

const filterVisitLetterIssues = (rawIssues, selectedVisitIds) => {
  const raw = rawIssues || [];
  const siteOpenResolved = raw.filter((item) => !isActionItemDbClosed(item.status));
  if (!selectedVisitIds?.length) {
    return siteOpenResolved;
  }
  const sel = new Set(selectedVisitIds.map(Number));
  const closedForSelected = raw.filter(
    (item) =>
      isActionItemDbClosed(item.status) && sel.has(getIssueScheduledVisitId(item))
  );
  const seen = new Set(siteOpenResolved.map(getActionItemDedupeKey));
  const merged = [...siteOpenResolved];
  closedForSelected.forEach((item) => {
    const key = getActionItemDedupeKey(item);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(item);
    }
  });
  return merged;
};

const VisitLetterPreviewPanel = ({
  letterKind,
  header,
  body,
  footer,
  visitMeta = {},
  projectCode,
  siteId,
  onReady,
}) => {
  const bodyRef = useRef(null);
  const kind = normalizeLetterKind(letterKind);
  const parts = useMemo(
    () => splitLetterContent(header, body, footer),
    [header, body, footer]
  );

  const [appendixIssues, setAppendixIssues] = useState([]);
  const [siteVisitsPickerList, setSiteVisitsPickerList] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [useStoredIssuesFallback, setUseStoredIssuesFallback] = useState(false);

  const visitContext = useMemo(() => ({
    scheduledVisitID: visitMeta.scheduledVisitId ?? visitMeta.ScheduledVisitId,
    siteCode: visitMeta.siteCode ?? visitMeta.SiteCode,
    visitType: visitMeta.visitType ?? visitMeta.VisitType,
    projectCode: projectCode || visitMeta.projectCode || visitMeta.ProjectCode,
    displayVisitCode: visitMeta.displayVisitCode ?? visitMeta.DisplayVisitCode,
  }), [visitMeta, projectCode]);

  useEffect(() => {
    let cancelled = false;
    setUseStoredIssuesFallback(false);
    setAppendixIssues([]);
    setSiteVisitsPickerList([]);

    const siteIdNum = siteId != null ? Number(siteId) : NaN;
    const pc = (projectCode || visitContext.projectCode || '').toString().trim();
    if (!Number.isFinite(siteIdNum) || siteIdNum <= 0 || !pc) {
      setIssuesLoading(false);
      if (parts.issuesHtml) setUseStoredIssuesFallback(true);
      return undefined;
    }

    setIssuesLoading(true);

    (async () => {
      try {
        if (kind === 'followup') {
          const scheduledVisitNum = Number(visitContext.scheduledVisitID);
          const params = { siteId: siteIdNum, projectCode: pc };
          if (Number.isFinite(scheduledVisitNum) && scheduledVisitNum > 0) {
            params.scheduledVisitId = scheduledVisitNum;
          }
          const res = await api.get('/crf/visit-report-site-appendix', { params });
          if (cancelled) return;
          const data = res?.data ?? {};
          setAppendixIssues(Array.isArray(data.openIssues) ? data.openIssues : []);
        } else {
          const [appendixRes, siteVisits] = await Promise.all([
            api.get('/crf/visit-report-site-appendix', {
              params: { siteId: siteIdNum, projectCode: pc },
            }),
            visitService.getSiteVisits(siteIdNum),
          ]);
          if (cancelled) return;
          const all = Array.isArray(appendixRes?.data?.openIssues)
            ? appendixRes.data.openIssues
            : [];
          const sameProjectVisits = (siteVisits || []).filter((v) =>
            visitMatchesLetterPickerScope({ projectCode: pc }, v)
          );
          const pickerList = buildSiteVisitsPickerList(sameProjectVisits, visitContext);
          setSiteVisitsPickerList(pickerList);
          setAppendixIssues(all);
        }
      } catch (_err) {
        if (!cancelled && parts.issuesHtml) {
          setUseStoredIssuesFallback(true);
        }
      } finally {
        if (!cancelled) setIssuesLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [kind, siteId, projectCode, visitContext, parts.issuesHtml]);

  useEffect(() => {
    if (issuesLoading || kind !== 'visit') return;
    if (siteVisitsPickerList.length === 0 && parts.issuesHtml) {
      setUseStoredIssuesFallback(true);
    }
  }, [issuesLoading, kind, siteVisitsPickerList.length, parts.issuesHtml]);

  useEffect(() => {
    if (!onReady) return undefined;
    if (issuesLoading) return undefined;
    const t = requestAnimationFrame(() => onReady());
    return () => cancelAnimationFrame(t);
  }, [issuesLoading, onReady, parts, appendixIssues, useStoredIssuesFallback]);

  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    root.querySelectorAll('a').forEach((anchorEl) => {
      const text = (anchorEl.textContent ?? '').trim().toLowerCase();
      if (text === 'approve' || text === 'reject') {
        anchorEl.removeAttribute('href');
        anchorEl.setAttribute('tabindex', '-1');
        anchorEl.setAttribute('aria-disabled', 'true');
      }
    });
  }, [parts.bodyHtml]);

  const selectedVisitsSummary = parts.visitsSummaryFromStored || '—';

  const visitLetterIssues = useMemo(() => {
    if (kind !== 'visit') return [];
    if (siteVisitsPickerList.length === 0) return [];
    return filterVisitLetterIssues(appendixIssues, []);
  }, [kind, appendixIssues, siteVisitsPickerList]);

  const showVisitLetterIssuesSection = kind === 'visit' && (
    siteVisitsPickerList.length > 0
    || parts.issuesHtml
    || issuesLoading
    || useStoredIssuesFallback
  );

  const renderIssuesTableRows = (issues, visit, emptyMessage) => {
    const fallbackSite = visit?.siteCode ?? visit?.SiteCode ?? '';
    if (issuesLoading) {
      return (
        <tr>
          <td colSpan={8} className="follow-up-open-issues-empty">Loading issues...</td>
        </tr>
      );
    }
    if (!issues.length) {
      return (
        <tr className="follow-up-open-issues-not-found-row">
          <td colSpan={8}>{emptyMessage}</td>
        </tr>
      );
    }
    return issues.map((item) => {
      const siteCell = [item.siteCode, item.siteDescription].filter(Boolean).join(' – ')
        || fallbackSite
        || '-';
      const vd = item.visitDate ?? item.VisitDate ?? item.plannedStartDate;
      return (
        <tr key={item.actionItemID ?? item.nCRFActionItemNo ?? item.actionItemNumber ?? `${siteCell}-${item.actionItemOn}`}>
          <td>{item.actionItemNumber ?? item.vActionItemNumber ?? '-'}</td>
          <td>{siteCell}</td>
          <td>{vd ? formatDateDDMMMYYYY(vd) : '-'}</td>
          <td>{item.actionItemOn || '-'}</td>
          <td>{item.findingDescription || '-'}</td>
          <td>{item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-'}</td>
          <td>{item.priorityDescription ?? item.severity ?? '-'}</td>
          <td>{formatActionItemStatusForDisplay(item.status)}</td>
        </tr>
      );
    });
  };

  const renderVisitLetterIssuesSection = () => {
    if (!showVisitLetterIssuesSection) return null;

    if (useStoredIssuesFallback && parts.issuesHtml) {
      return (
        <div dangerouslySetInnerHTML={{ __html: parts.issuesHtml }} />
      );
    }

    return (
      <div className="follow-up-open-issues-section">
        <div className="generate-letter-visit-filter generate-letter-visit-filter--preview">
          <div className="generate-letter-selected-visits-text">
            <span className="generate-letter-selected-visits-label">Selected visits:</span>{' '}
            <span className="generate-letter-selected-visits-value">{selectedVisitsSummary}</span>
          </div>
        </div>
        <h3 className="follow-up-open-issues-title">Issues</h3>
        <div className="table-responsive">
          <table className="follow-up-open-issues-table">
            <thead>
              <tr>
                <th>Reference No</th>
                <th>Site</th>
                <th>Visit Date</th>
                <th>Field Name</th>
                <th>Issue Description</th>
                <th>Expected Closure Date</th>
                <th>Priority</th>
                <th>Current Status</th>
              </tr>
            </thead>
            <tbody>
              {renderIssuesTableRows(
                visitLetterIssues,
                visitContext,
                'Issues are not found'
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFollowUpIssuesSection = () => {
    if (useStoredIssuesFallback && parts.issuesHtml) {
      return <div dangerouslySetInnerHTML={{ __html: parts.issuesHtml }} />;
    }

    return (
    <div className="follow-up-open-issues-section">
        <>
          <h3 className="follow-up-open-issues-title">Issues</h3>
          <div className="table-responsive">
            <table className="follow-up-open-issues-table">
              <thead>
                <tr>
                  <th>Reference No</th>
                  <th>Site</th>
                  <th>Visit Date</th>
                  <th>Field Name</th>
                  <th>Issue Description</th>
                  <th>Expected Closure Date</th>
                  <th>Priority</th>
                  <th>Current Status</th>
                </tr>
              </thead>
              <tbody>
                {renderIssuesTableRows(
                  appendixIssues,
                  visitContext,
                  'No issues for this site.'
                )}
              </tbody>
            </table>
          </div>
        </>
    </div>
    );
  };

  return (
    <div className="generate-letter-preview-content visit-letter-downloads-preview">
      {parts.headerText ? (
        <pre className="generate-letter-preview-header">{parts.headerText}</pre>
      ) : null}
      {parts.bodyHtml ? (
        <div
          ref={bodyRef}
          className="generate-letter-preview-body"
          dangerouslySetInnerHTML={{ __html: parts.bodyHtml }}
        />
      ) : (
        <div ref={bodyRef} className="generate-letter-preview-body" />
      )}
      {kind === 'followup' ? renderFollowUpIssuesSection() : renderVisitLetterIssuesSection()}
      {parts.footerText ? (
        <pre className="generate-letter-preview-footer">{parts.footerText}</pre>
      ) : null}
      {parts.reportCreatedByHtml ? (
        <div dangerouslySetInnerHTML={{ __html: parts.reportCreatedByHtml }} />
      ) : null}
    </div>
  );
};

export default VisitLetterPreviewPanel;
